import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  IconHome,
  IconActivity,
  IconMapPin,
  IconChartBar,
  IconAdjustmentsHorizontal,
  IconSettings,
  IconUsers,
  IconTrophy,
  IconLayoutKanban,
  IconCalendar,
  IconFilter,
  IconRuler,
  IconBook2,
  IconTargetArrow,
  IconInbox,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@/contexts/NavigationContext';
import type { NavigationSection } from '@/types/navigation';
import type { ModuleConfig } from '@/components/ui/module-top-bar/ModuleTopBar';
import type { SubNavTab } from '@/components/ui/module-sub-nav/ModuleSubNav';
import { usePendingOrderCount } from './usePendingOrderCount';
import { useInboxCount } from './useInboxCount';
import { useHqQueueCount } from './useHqQueueCount';
import { useAuth } from '@/hooks/useAuth';
import { useCanSeeTickets } from '@/hooks/useSupportTickets';


// Note: 'sliders' is the API iconName for plan-management; maps to same component as 'adjustmentsHorizontal'
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  home: IconHome,
  activity: IconActivity,
  mapPin: IconMapPin,
  chartBar: IconChartBar,
  adjustmentsHorizontal: IconAdjustmentsHorizontal,
  sliders: IconAdjustmentsHorizontal, // API uses 'sliders' for plan-management
  settings: IconSettings,
  users: IconUsers,
  trophy: IconTrophy,
  'layout-kanban': IconLayoutKanban,
  bookOpen: IconBook2, // Content Library section icon (Phase 314)
  calendar: IconCalendar,
  filter: IconFilter,
  ruler: IconRuler,
  gamePlan: IconTargetArrow, // Game Plan section icon
  inbox: IconInbox, // Inbox section icon (Phase 385)
};

/** A module plus the pages beneath it — drives the mobile drawer's tree. */
export interface ModuleNavNode extends ModuleConfig {
  children: SubNavTab[];
}

/** A destination in the mobile bottom tab bar. */
export interface MobileTab {
  key: string;
  label: string;
  href: string;
  icon: React.ReactNode;
}

export interface ModuleConfigResult {
  currentModule: ModuleConfig | null;
  allModules: ModuleConfig[];
  subNavTabs: SubNavTab[];
  /**
   * Every module with its child pages nested underneath. `subNavTabs` only ever
   * describes the *current* module, which is all the desktop sub-nav bar needs;
   * the mobile drawer shows the whole hierarchy at once, so it needs this.
   */
  moduleTree: ModuleNavNode[];
  /**
   * The handful of destinations worth a permanent bottom tab on mobile.
   * Derived from the same permission-filtered navigation data as everything
   * else, so a user without Pulse simply gets three tabs rather than a dead one.
   */
  mobileTabs: MobileTab[];
  /**
   * True when the current route is a module landing page or one of its sub-nav
   * pages — i.e. a "first level" screen. Detail, create and edit routes are not,
   * and hide the tab bar so it doesn't compete with their own actions.
   */
  isFirstLevelRoute: boolean;
  /** True when the server returned the Inbox section (Conversations access). */
  hasInbox: boolean;
  /** Unread inbox count for the selected inbox location (0 without access). */
  inboxCount: number;
  /** True when the server returned the Issue Centre page (issue notice access). */
  hasIssueNotices: boolean;
  /** True when the server-gated nav tree includes the HQ Queue page (support-tickets). */
  hasHqQueue: boolean;
  /** Open-ticket count for the global HQ Queue badge (0 without access). */
  hqQueueCount: number;
  loading: boolean;
}

/**
 * A module whose only entries are launcher tiles (launcherOnly: true) has no
 * page to navigate to inside box-os — every node is either an external link
 * (e.g. forj-fit) or a launcher-only stub. Surfacing such a module in the
 * top nav or mobile drawer would be a dead link, so both `allModules` and
 * `moduleTree` require at least one non-launcherOnly node. This is what keeps
 * "Account Settings" out of a non-admin role's navigation now that the
 * `alwaysVisible` forj-fit node alone can keep that section present in the
 * API payload (see api/src/modules/navigation/navigation.service.ts).
 */
function hasNavigableNode(section: NavigationSection): boolean {
  return (section.nodes ?? []).some((node) => !node.launcherOnly);
}

export function useModuleConfig(): ModuleConfigResult {
  const location = useLocation();
  const pathname = location.pathname;
  const { sections, loading } = useNavigation();
  const { t } = useTranslation();
  const { user } = useAuth();

  // Phase 385 (D-07): Inbox badge is global, gated on access rather than route.
  const hasInbox = React.useMemo(() => sections.some((s) => s.uid === 'inbox'), [sections]);
  const inboxCount = useInboxCount(hasInbox);
  const hasIssueNotices = React.useMemo(
    () => sections.some((s) => (s.nodes ?? []).some((n) => n.uid === 'issues-centre')),
    [sections],
  );

  // Same server gate as the HQ Queue nav entry itself — only HQ staff.
  const hasHqQueue = useCanSeeTickets();
  const hqQueueCount = useHqQueueCount(hasHqQueue);

  // D-03: filter sections where hasNodes === true — excludes dashboard (hasNodes:false)
  // Returns [] when sections not loaded yet (avoids flash during loading)
  const allModules: ModuleConfig[] = React.useMemo(() => {
    if (sections.length === 0) return [];
    return sections
      .filter((s) => s.hasNodes && hasNavigableNode(s))
      .map((s) => {
        const IconComponent = ICON_MAP[s.iconName] ?? null;
        return {
          key: s.uid,
          icon: IconComponent ? React.createElement(IconComponent, { size: 16 }) : null,
          name: t(s.labelKey),
          href: s.landingUrl,
          ...(s.uid === 'inbox' && inboxCount > 0 ? { badge: inboxCount } : {}),
        };
      });
  }, [sections, t, inboxCount]);

  // Longest-prefix match against section.nodes[].url and section.landingUrl
  const activeSection: NavigationSection | null = React.useMemo(() => {
    if (pathname === '/') return null; // launcher — no active module

    let best: NavigationSection | null = null;
    let bestLen = 0;

    for (const s of sections) {
      // Check each node URL
      for (const node of s.nodes ?? []) {
        if (pathname === node.url || pathname.startsWith(node.url + '/')) {
          if (node.url.length > bestLen) {
            bestLen = node.url.length;
            best = s;
          }
        }
      }
      // Also match on landingUrl for sections whose landing URL isn't in nodes[]
      if (
        s.landingUrl !== '/' &&
        (pathname === s.landingUrl || pathname.startsWith(s.landingUrl + '/'))
      ) {
        if (s.landingUrl.length > bestLen) {
          bestLen = s.landingUrl.length;
          best = s;
        }
      }
    }

    return best;
  }, [pathname, sections]);

  const currentModule: ModuleConfig | null = React.useMemo(() => {
    if (!activeSection) return null;
    const IconComponent = ICON_MAP[activeSection.iconName] ?? null;
    return {
      key: activeSection.uid,
      icon: IconComponent ? React.createElement(IconComponent, { size: 16 }) : null,
      name: t(activeSection.labelKey),
      href: activeSection.landingUrl,
      ...(activeSection.uid === 'inbox' && inboxCount > 0 ? { badge: inboxCount } : {}),
    };
  }, [activeSection, t, inboxCount]);

  // D-04: gate pending-order query on club-sales section being active
  const isClubSalesRoute = activeSection?.uid === 'club-sales';
  const pendingOrderCount = usePendingOrderCount(isClubSalesRoute);

  // Phase 357: member-management node visibility (including the coach denies
  // added to api/src/modules/navigation/config/navigation.ts) is entirely
  // server-derived from activeSection.nodes — denied nodes never reach this
  // hook. No client-side role filter belongs here: it would drift from
  // navigation.ts and would be cosmetic regardless (see 357-03-PLAN.md).
  // CLAUDE.md's "two files always" navigation rule does not apply to this
  // change — this file has no per-module sub-nav-tab lookup function keyed
  // on a hardcoded pathname prefix to pair the update with; subNavTabs below
  // is derived purely from the API response.
  const subNavTabs: SubNavTab[] = React.useMemo(() => {
    if (!activeSection || !activeSection.nodes?.length) return [];

    const visibleNodes = activeSection.nodes.filter((node) => !node.launcherOnly && !node.hiddenFromSubNav);
    const tabs = visibleNodes.map((node) => {
      const tab: SubNavTab = {
        label: t(node.labelKey),
        href: node.url,
      };
      // Badge: inject pendingOrderCount on the approve-orders node (canonical uid from API config)
      // Source: api/src/modules/navigation/config/navigation.ts — uid: 'club-sales-approve-orders'
      if (node.uid === 'club-sales-approve-orders' && pendingOrderCount > 0) {
        tab.badge = pendingOrderCount;
      }
      if (node.uid === 'inbox-topics' && inboxCount > 0) {
        tab.badge = inboxCount;
      }
      return tab;
    });

    // SUPER_ADMIN-only FAQ Management tab on /help pages (D-08: defense in depth alongside
    // server-side Modules.FAQ permission gate; role name 'admin' = SUPER_ADMIN_NAME)
    if (activeSection.uid === 'product-support' && user?.roleName === 'admin') {
      const faqMgmtHref = '/faq/manage';
      if (!tabs.some((tab) => tab.href === faqMgmtHref)) {
        tabs.push({ label: t('navigation.faqManagement'), href: faqMgmtHref });
      }
    }

    return tabs;
  }, [activeSection, pendingOrderCount, inboxCount, t, user?.roleName]);

  const moduleTree: ModuleNavNode[] = React.useMemo(() => {
    if (sections.length === 0) return [];
    return sections
      .filter((section) => section.hasNodes && hasNavigableNode(section))
      .map((section) => {
        const IconComponent = ICON_MAP[section.iconName] ?? null;
        const children = (section.nodes ?? [])
          .filter((node) => !node.launcherOnly && !node.hiddenFromSubNav)
          .map((node) => {
            const tab: SubNavTab = { label: t(node.labelKey), href: node.url };
            if (node.uid === 'club-sales-approve-orders' && pendingOrderCount > 0) {
              tab.badge = pendingOrderCount;
            }
            if (node.uid === 'inbox-topics' && inboxCount > 0) {
              tab.badge = inboxCount;
            }
            return tab;
          });

        // Mirror the SUPER_ADMIN-only FAQ Management tab the sub-nav adds.
        if (section.uid === 'product-support' && user?.roleName === 'admin') {
          const faqMgmtHref = '/faq/manage';
          if (!children.some((child) => child.href === faqMgmtHref)) {
            children.push({ label: t('navigation.faqManagement'), href: faqMgmtHref });
          }
        }

        return {
          key: section.uid,
          icon: IconComponent ? React.createElement(IconComponent, { size: 18 }) : null,
          name: t(section.labelKey),
          href: section.landingUrl,
          children,
          ...(section.uid === 'inbox' && inboxCount > 0 ? { badge: inboxCount } : {}),
        };
      });
  }, [sections, pendingOrderCount, inboxCount, t, user?.roleName]);

  const mobileTabs: MobileTab[] = React.useMemo(() => {
    if (sections.length === 0) return [];

    const iconFor = (name: string, size = 20) => {
      const Cmp = ICON_MAP[name] ?? null;
      return Cmp ? React.createElement(Cmp, { size }) : null;
    };

    // Bottom tabs get ~a quarter of the screen each, so a couple of them need
    // a shorter label than the module carries elsewhere. Overrides are keyed by
    // section uid and resolve through i18n like any other label.
    const TAB_LABEL_OVERRIDES: Record<string, string> = {
      'member-management': 'navigation.members', // "Member Management" is too long for a tab
    };

    const tabs: MobileTab[] = [];

    // Calendar is a node inside the franchise-management section rather than a
    // section of its own, so it is looked up by url instead of by section uid.
    for (const section of sections) {
      const calendarNode = (section.nodes ?? []).find((n) => n.url === '/calendar');
      if (calendarNode) {
        tabs.push({
          key: 'calendar',
          label: t(calendarNode.labelKey),
          href: calendarNode.url,
          icon: iconFor(calendarNode.iconName ?? 'calendar'),
        });
        break;
      }
    }

    for (const uid of ['franchise-mgmt', 'member-management', 'pulse']) {
      const section = sections.find((sec) => sec.uid === uid && sec.hasNodes);
      if (!section) continue;
      tabs.push({
        key: section.uid,
        label: t(TAB_LABEL_OVERRIDES[section.uid] ?? section.labelKey),
        href: section.landingUrl,
        icon: iconFor(section.iconName),
      });
    }

    return tabs;
  }, [sections, t]);

  const isFirstLevelRoute = React.useMemo(() => {
    if (pathname === '/') return false; // launcher has its own layout
    if (activeSection?.landingUrl === pathname) return true;
    // Exact match against a sub-nav href only: /members/overview is first level,
    // /members/:id/view is not, and neither is /plans/create.
    return subNavTabs.some((tab) => tab.href === pathname);
  }, [pathname, activeSection, subNavTabs]);

  return {
    currentModule,
    allModules,
    subNavTabs,
    moduleTree,
    mobileTabs,
    isFirstLevelRoute,
    hasInbox,
    inboxCount,
    hasIssueNotices,
    hasHqQueue,
    hqQueueCount,
    loading,
  };
}
